import React, { useState, useEffect, useRef } from 'react';

interface DropdownOption {
  label: string;
  value: string;
}

interface FormDropdownProps {
  id: string;
  value: string;
  options: DropdownOption[];
  placeholder?: string;
  floatingLabel?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-required'?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

const FormDropdown: React.FC<FormDropdownProps> = ({
  id,
  value,
  options,
  placeholder = 'Select an option',
  floatingLabel,
  onChange,
  onBlur,
  error,
  required,
  disabled,
  className = '',
  ...ariaProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasValue = Boolean(value);
  const hasError = Boolean(error);

  // Get the selected option label for display
  const selectedOption = options?.find((opt) => opt.value === value);
  const selectedLabel = selectedOption?.label || '';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        if (isOpen) {
          setIsOpen(false);
          setTouched(true);
          // Always trigger validation when closing dropdown (user has interacted)
          if (onBlur) {
            onBlur();
          }
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onBlur, isOpen]);

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setTouched(true);
    // Don't call onBlur here - onChange already handles validation
  };

  const handleToggleDropdown = () => {
    if (disabled) return;

    // If closing the dropdown, trigger validation
    if (isOpen) {
      setTouched(true);
      if (onBlur) {
        onBlur();
      }
    }
    setIsOpen(!isOpen);
  };

  const getFieldErrorClasses = () => {
    return hasError
      ? 'border-red-500 focus:ring-red-500 bg-red-50'
      : 'border-gray-300 focus:ring-blue-500';
  };

  return (
    <div className={`max-w-[1000px] ${className}`} ref={dropdownRef}>
      <div className="relative">
        {/* Hidden select for accessibility and form submission */}
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          className="sr-only"
          tabIndex={-1}
          {...ariaProps}
        >
          <option value="">{placeholder}</option>
          {options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Custom dropdown display */}
        <div
          onClick={handleToggleDropdown}
          className={`w-full px-3 pr-10 ${
            hasValue ? 'pt-6 pb-2 min-h-[56px]' : 'py-3'
          } border rounded-sm cursor-pointer flex items-center ${getFieldErrorClasses()} ${
            disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''
          }`}
          role="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              handleToggleDropdown();
            }
          }}
        >
          <span
            className={`flex-1 ${hasValue ? 'text-gray-900' : 'text-gray-500'} break-words pr-2`}
          >
            {hasValue ? selectedLabel : placeholder}
          </span>
          <svg
            className={`w-5 h-5 text-gray-600 transition-transform flex-shrink-0 ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>

        {/* Floating label */}
        {hasValue && floatingLabel && (
          <label
            htmlFor={id}
            className="absolute left-3 top-1 text-xs text-gray-600 pointer-events-none z-10"
          >
            {floatingLabel}
          </label>
        )}

        {/* Dropdown options */}
        {isOpen && !disabled && (
          <div
            className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-sm shadow-lg max-h-60 overflow-y-auto"
            role="listbox"
          >
            {options?.map((option) => (
              <div
                key={option.value}
                onClick={() => handleOptionClick(option.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOptionClick(option.value);
                  }
                }}
                className={`px-3 py-3 cursor-pointer hover:bg-gray-100 break-words ${
                  value === option.value
                    ? 'bg-blue-50 text-blue-900'
                    : 'text-gray-900'
                }`}
                role="option"
                aria-selected={value === option.value}
                tabIndex={0}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FormDropdown;
